/**
 * OSS前端直传工具
 * 使用STS临时凭证实现前端直传阿里云OSS
 */
import OSS from 'ali-oss'
import axios from 'axios'

let ossClient = null
let tokenExpiration = null

// 格式化时间为 yyyyMMddHHmmss
const formatDateTime = () => {
    const now = new Date()
    const pad = (n) => n.toString().padStart(2, '0')
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
}

/**
 * 获取或刷新OSS客户端
 */
export const getOssClient = async () => {
    // 检查token是否过期
    if (ossClient && tokenExpiration && new Date() < new Date(tokenExpiration)) {
        return ossClient
    }

    try {
        const res = await axios.get('/oss/sts-token')
        const response = res.data

        // 获取实际的 token 数据（可能在 data 字段中）
        const tokenData = response.data || response.results || response

        // 适配字段名：支持大驼峰和小驼峰两种格式
        const accessKeyId = tokenData.AccessKeyId || tokenData.accessKeyId
        const accessKeySecret = tokenData.AccessKeySecret || tokenData.accessKeySecret
        const stsToken = tokenData.SecurityToken || tokenData.securityToken
        const region = tokenData.Region || tokenData.region
        const bucket = tokenData.Bucket || tokenData.bucket
        const expiration = tokenData.Expiration || tokenData.expiration

        if (!accessKeyId || !accessKeySecret) {
            throw new Error('获取凭证失败：缺少必要的凭证信息')
        }

        ossClient = new OSS({
            region: region,
            accessKeyId: accessKeyId,
            accessKeySecret: accessKeySecret,
            stsToken: stsToken,
            bucket: bucket
        })

        tokenExpiration = expiration
        return ossClient
    } catch (e) {
        console.error('获取OSS凭证失败:', e)
        throw e
    }
}

// 清理文件名中的特殊字符（括号、空格等），避免 OSS CORS 问题
const sanitizeFileName = (name) => {
    return name.replace(/[()（）\[\]\s#%&{}|\\^~`@!$*+,;=]/g, '_')
}

/**
 * 普通上传（小文件）
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadFile = async (file, path) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${sanitizeFileName(file.name)}`
    const result = await client.put(objectName, file)

    // 确保 URL 是 https
    let url = result.url
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    return {
        url: signedUrl,
        name: result.name,
        originalName: file.name
    }
}

/**
 * 分片上传（带进度，适合大文件）
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @param {Function} onProgress - 进度回调 (percent: number) => void
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadWithProgress = async (file, path, onProgress) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${sanitizeFileName(file.name)}`

    console.log('[OSS Upload] Starting multipart upload:', objectName, 'size:', file.size)

    const result = await client.multipartUpload(objectName, file, {
        progress: async (p, checkpoint) => {
            const percent = Math.round(p * 100)
            console.log('[OSS Upload] Progress:', percent + '%')
            onProgress && onProgress(percent)
        },
        partSize: 1024 * 1024, // 1MB per part
        parallel: 3 // 并行上传数
    })

    console.log('[OSS Upload] Complete')

    // 确保 URL 是 https
    let url = result.res.requestUrls[0].split('?')[0]
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    return {
        url: signedUrl,
        name: result.name,
        originalName: file.name
    }
}

/**
 * 判断文件大小决定使用哪种上传方式
 * @param {File} file - 文件对象
 * @param {string} path - 存储路径前缀
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<{url: string, name: string}>}
 */
export const smartUpload = async (file, path, onProgress) => {
    const THRESHOLD = 5 * 1024 * 1024 // 5MB

    if (file.size > THRESHOLD) {
        return uploadWithProgress(file, path, onProgress)
    } else {
        const result = await uploadFile(file, path)
        onProgress && onProgress(100)
        return result
    }
}

/**
 * 删除 OSS 文件（通过后端 API）
 * @param {string} url - 文件 URL
 * @returns {Promise<boolean>}
 */
export const deleteFile = async (url) => {
    try {
        // 从 URL 中提取 object key
        // URL 格式: https://bucket.region.aliyuncs.com/path/to/file.jpg
        const urlObj = new URL(url)
        const objectKey = decodeURIComponent(urlObj.pathname.substring(1)) // 去掉开头的 /

        console.log('[OSS Delete] Deleting via backend API:', objectKey)

        const res = await axios.post('/pc/oss-delete', { objectKey })
        const data = res.data?.data || res.data

        if (data.success) {
            console.log('[OSS Delete] Success')
            return true
        } else {
            console.warn('[OSS Delete] Failed:', data.message)
            return false
        }
    } catch (e) {
        console.warn('[OSS Delete] Failed:', e.message)
        return false
    }
}

/**
 * 对 OSS URL 进行签名（通过后端 API）
 * @param {string} url - 原始 URL
 * @returns {Promise<string>} - 签名后的 URL
 */
export const signFile = async (url) => {
    try {
        if (!url) return url
        const res = await axios.post('/pc/oss-sign', { url })
        const data = res.data?.data || res.data
        if (data.success && data.signedUrl) {
            return data.signedUrl
        }
        return url
    } catch (e) {
        console.warn('[OSS Sign] Failed:', e)
        return url
    }
}

/**
 * 上传 Blob 数据（如录音）
 * @param {Blob} blob - Blob 数据
 * @param {string} path - 存储路径前缀
 * @param {string} filename - 文件名
 * @returns {Promise<{url: string, name: string}>}
 */
export const uploadBlob = async (blob, path, filename) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${filename}`

    console.log('[OSS Upload] Uploading blob:', objectName, 'size:', blob.size)

    const result = await client.put(objectName, blob)

    // 确保 URL 是 https
    let url = result.url
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名（支持私有 Bucket）
    const signedUrl = await signFile(url)

    console.log('[OSS Upload] Blob upload complete:', signedUrl)

    return {
        url: signedUrl,
        name: result.name,
        originalName: filename
    }
}

/**
 * 初始化分片上传（获取uploadId）
 * @param {string} path - 存储路径前缀
 * @param {string} filename - 文件名
 * @returns {Promise<{uploadId: string, objectName: string}>}
 */
export const initMultipartUpload = async (path, filename) => {
    const client = await getOssClient()
    const objectName = `${path}/${formatDateTime()}_${filename}`

    console.log('[OSS Multipart] Initializing:', objectName)

    const result = await client.initMultipartUpload(objectName)

    console.log('[OSS Multipart] Initialized, uploadId:', result.uploadId)

    return {
        uploadId: result.uploadId,
        objectName: result.name
    }
}

/**
 * 上传单个分片
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @param {number} partNumber - 分片编号（从1开始）
 * @param {Blob} blob - 分片数据
 * @returns {Promise<{etag: string, partNumber: number}>}
 */
export const uploadPart = async (objectName, uploadId, partNumber, blob) => {
    const client = await getOssClient()

    console.log('[OSS Multipart] Uploading part:', partNumber, 'size:', blob.size)

    const result = await client.uploadPart(objectName, uploadId, partNumber, blob)

    console.log('[OSS Multipart] Part uploaded:', partNumber, 'etag:', result.etag)

    return {
        etag: result.etag,
        partNumber: partNumber
    }
}

/**
 * 完成分片上传（合并文件）
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @param {Array<{partNumber: number, etag: string}>} parts - 已上传分片列表
 * @returns {Promise<{url: string, name: string}>}
 */
export const completeMultipartUpload = async (objectName, uploadId, parts) => {
    const client = await getOssClient()

    console.log('[OSS Multipart] Completing upload:', objectName, 'parts:', parts.length, 'raw parts:', JSON.stringify(parts))

    // 验证并过滤有效的parts
    const validParts = parts.filter(p => {
        const isValid = p &&
            typeof p.partNumber === 'number' &&
            !isNaN(p.partNumber) &&
            p.partNumber > 0 &&
            typeof p.etag === 'string' &&
            p.etag.length > 0
        if (!isValid) {
            console.warn('[OSS Multipart] Invalid part:', p)
        }
        return isValid
    })

    if (validParts.length === 0) {
        throw new Error('没有有效的分片数据')
    }

    // 按partNumber去重（保留最后一个）并排序
    const partsMap = new Map()
    for (const part of validParts) {
        // ali-oss SDK 要求使用 number 属性（不是 partNumber）
        partsMap.set(part.partNumber, {
            number: part.partNumber,
            etag: part.etag.replace(/"/g, '') // 确保etag没有引号
        })
    }
    const sortedParts = Array.from(partsMap.values()).sort((a, b) => a.number - b.number)

    console.log('[OSS Multipart] Final sorted parts count:', sortedParts.length)
    console.log('[OSS Multipart] First 3 parts:', JSON.stringify(sortedParts.slice(0, 3)))
    console.log('[OSS Multipart] Last 3 parts:', JSON.stringify(sortedParts.slice(-3)))

    const result = await client.completeMultipartUpload(objectName, uploadId, sortedParts)

    // 确保 URL 是 https
    let url = result.res.requestUrls[0].split('?')[0]
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://')
    }

    // 对 URL 进行签名
    const signedUrl = await signFile(url)

    console.log('[OSS Multipart] Complete:', signedUrl)

    return {
        url: signedUrl,
        name: result.name
    }
}

/**
 * 取消分片上传
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 */
export const abortMultipartUpload = async (objectName, uploadId) => {
    try {
        const client = await getOssClient()
        await client.abortMultipartUpload(objectName, uploadId)
        console.log('[OSS Multipart] Aborted:', objectName)
        return true
    } catch (e) {
        console.warn('[OSS Multipart] Abort failed:', e)
        return false
    }
}

/**
 * 列出已上传的分片
 * @param {string} objectName - 对象名称
 * @param {string} uploadId - 分片上传ID
 * @returns {Promise<Array<{partNumber: number, etag: string, size: number}>>}
 */
export const listParts = async (objectName, uploadId) => {
    try {
        const client = await getOssClient()
        const result = await client.listParts(objectName, uploadId)

        console.log('[OSS Multipart] Listed parts:', result.parts?.length || 0)

        return (result.parts || []).map(p => ({
            partNumber: p.partNumber,
            etag: p.etag,
            size: p.size
        }))
    } catch (e) {
        console.warn('[OSS Multipart] List parts failed:', e)
        return []
    }
}

export default {
    getOssClient,
    uploadFile,
    uploadWithProgress,
    smartUpload,
    deleteFile,
    signFile,
    uploadBlob,
    initMultipartUpload,
    uploadPart,
    completeMultipartUpload,
    abortMultipartUpload,
    listParts
}

